import axios, { AxiosError } from 'axios'
import { useNotificationStore } from '@/website/stores/notification'
import type { Requests, Responses } from '@/website/exported-types/requests-and-responses'


export type ErrorResponse = {
    error: string,
    details?: string,
    [key: string]: unknown,
}


function isErrorResponse(data: any): data is ErrorResponse {
    return data
        && typeof data === 'object'
        && 'error' in data
        && typeof data.error === 'string'
}


type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD' | 'OPTIONS'

type RequestBody<Path extends keyof Requests, M extends Method> =
    Path extends keyof Requests
        ? M extends keyof Requests[Path]
            ? 'body' extends keyof Requests[Path][M]
                ? Requests[Path][M]['body']
                : undefined
            : undefined
        : undefined

type RequestQuery<Path extends keyof Requests, M extends Method> =
    Path extends keyof Requests
        ? M extends keyof Requests[Path]
            ? 'query' extends keyof Requests[Path][M]
                ? Requests[Path][M]['query']
                : undefined
            : undefined
        : undefined

type SuccessResponse<Path extends keyof Responses, M extends Method> =
    Path extends keyof Responses
        ? M extends keyof Responses[Path]
            ? Responses[Path][M][Extract<keyof Responses[Path][M], `2${string}`>]
            : never
        : never


type HttpConfig<Path extends keyof Requests | keyof Responses, M extends Method> = {
    data?: RequestBody<Path extends keyof Requests ? Path : never, M>,
    query?: RequestQuery<Path extends keyof Requests ? Path : never, M>,
    urlParams?: Record<string, number|string>,
    useFormData?: boolean,
    hasOfflineFallback?: boolean,
    success?: (response: SuccessResponse<Path extends keyof Responses ? Path : never, M>) => void,
    error?: undefined,
    always?: () => void,
}

type HttpConfigWithErrorHandler<Path extends keyof Requests | keyof Responses, M extends Method> = Omit<HttpConfig<Path, M>, 'error'> & {
    error: (response?: ErrorResponse, error?: AxiosError) => void,
}


export default class Http {
    static async request<Path extends keyof Requests | keyof Responses, M extends Method>(
        method: M,
        url: Path,
        config: HttpConfig<Path, M>,
    ): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, M>|ErrorResponse>

    static async request<Path extends keyof Requests | keyof Responses, M extends Method>(
        method: M,
        url: Path,
        config: HttpConfigWithErrorHandler<Path, M>,
    ): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, M>|null>

    static async request<Path extends keyof Requests | keyof Responses, M extends Method>(
        method: M,
        url: Path,
        config: HttpConfig<Path, M>|HttpConfigWithErrorHandler<Path, M> = {}
    ): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, M>|ErrorResponse|null> {

        if (config.urlParams) {
            for (const key in config.urlParams) {
                url = url.replace(`{${key}}`, String(config.urlParams[key])) as Path
            }
        }

        const requestData = config.useFormData ? toFormData(config.data as any) : config.data

        const sendAxiosRequest = () => axios<SuccessResponse<(Path extends keyof Responses ? Path : never), M>|ErrorResponse>({
            method,
            url,
            data: requestData,
            params: config.query,
        })

        try {
            const response = await sendAxiosRequest()

            if (config.error) {
                if (isErrorResponse(response.data)) {
                    config.error(response.data, undefined)
                }
            }

            if (config.success) {
                if (! isErrorResponse(response.data)) {
                    config.success(response.data)
                }
            }

            if (config.error && isErrorResponse(response.data)) {
                return null
            }

            return response.data

        } catch (error) {
            console.error(error)

            const axiosError = axios.isAxiosError(error) ? error : undefined

            if (config.error) {
                config.error(axiosError?.response?.data, axiosError)

                return null
            }

            if (axios.isAxiosError(error)) {
                return error.response?.data
            }

            return null

        } finally {
            if (config.always instanceof Function) {
                config.always()
            }
        }
    }

    static post<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfig<Path, 'POST'>): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'POST'>|ErrorResponse>
    static post<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfigWithErrorHandler<Path, 'POST'>): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'POST'>|null>
    static post<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfig<Path, 'POST'>|HttpConfigWithErrorHandler<Path, 'POST'> = {}): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'POST'>|ErrorResponse|null> {
        if (config.error) {
            return this.request('POST', url, config)
        }

        return this.request('POST', url, config)
    }

    static get<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfig<Path, 'GET'>): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'GET'>|ErrorResponse>
    static get<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfigWithErrorHandler<Path, 'GET'>): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'GET'>|null>
    static get<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfig<Path, 'GET'>|HttpConfigWithErrorHandler<Path, 'GET'> = {}): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'GET'>|ErrorResponse|null> {
        if (config.error) {
            return this.request('GET', url, config)
        }

        return this.request('GET', url, config)
    }

    static put<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfig<Path, 'PUT'>): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'PUT'>|ErrorResponse>
    static put<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfigWithErrorHandler<Path, 'PUT'>): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'PUT'>|null>
    static put<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfig<Path, 'PUT'>|HttpConfigWithErrorHandler<Path, 'PUT'> = {}): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'PUT'>|ErrorResponse|null> {
        if (config.error) {
            return this.request('PUT', url, config)
        }

        return this.request('PUT', url, config)
    }

    static delete<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfig<Path, 'DELETE'>): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'DELETE'>|ErrorResponse>
    static delete<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfigWithErrorHandler<Path, 'DELETE'>): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'DELETE'>|null>
    static delete<Path extends keyof Requests | keyof Responses>(url: Path, config: HttpConfig<Path, 'DELETE'>|HttpConfigWithErrorHandler<Path, 'DELETE'> = {}): Promise<SuccessResponse<Path extends keyof Responses ? Path : never, 'DELETE'>|ErrorResponse|null> {
        if (config.error) {
            return this.request('DELETE', url, config)
        }

        return this.request('DELETE', url, config)
    }
}



function toFormData(obj: Record<string, any>, form?: FormData, namespace?: string) {
    const fd = form || new FormData()

    for (const property in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, property) && obj[property]) {
            let formKey

            if (namespace) {
                formKey = namespace + '[' + property + ']'
            } else {
                formKey = property
            }

            if (obj[property] instanceof Date) {
                fd.append(formKey, obj[property].toISOString())

            } else if (typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
                toFormData(obj[property], fd, formKey)

            } else {
                // string|File
                fd.append(formKey, obj[property])
            }
        }
    }

    return fd
}



axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

axios.interceptors.response.use(response => response, async error => {
    const status = error?.response?.status

    if (status === 419) {
        // Refresh CSRF token
        await axios.get('/csrf-token')

        return axios(error.response.config)

    } else if (status === 401) {
        window.location.href = '/logout'
    }

    return Promise.reject(error)
})



export function websiteErrorHandler(options: {
    defaultError: string,
}) {
    return (response?: ErrorResponse, _error?: AxiosError) => {
        if (response?.error) {
            useNotificationStore().add('error', response.error, response.details)
            return
        }

        useNotificationStore().add('error', options.defaultError)
    }
}


export function gameErrorHandler(options: {
    defaultError: string,
}) {
    return (response?: ErrorResponse, error?: AxiosError) => {
        console.warn(options.defaultError, response, error)
    }
}
